import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRoleEnum } from '../enums/user-role-enum';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    let userInfo = this.authService.CurrentUserValue;

    // Retrieve required roles from the route's data
    const requiredRoles: string[] = route.data['roles'];

    if (userInfo && userInfo.id) {
      const isSuperAdmin = userInfo.role === UserRoleEnum.MasterAdmin;
      const hasProjectId = !!userInfo.project_id;

      // Check if the route is under 'project' path
      const isProjectRoute = state.url.startsWith('/project');
      const isSettingsRoute = state.url.startsWith('/settings'); // Added check for settings route
      const isProfileRoute = state.url.startsWith('/profile');

      // Handle access to 'project' routes
      if (isProjectRoute) {
        if (isSuperAdmin) {
          return true; // Super Admin can access 'project' routes
        } else {
          // Project Admin and User cannot access 'project' routes
          this.router.navigate(['/home']);
          return false;
        }
      }

      // Allow Super Admin to access settings page regardless of project_id
      if (isSettingsRoute && isSuperAdmin) {
        return true;
      }

      // Allow Super Admin to access settings page regardless of project_id
      if (isProfileRoute && isSuperAdmin) {
        return true;
      }

      // No projectId logic
      if (!hasProjectId) {
        if (isSuperAdmin) {
          // Super Admin without projectId, redirect to '/project/list'
          this.router.navigate(['/project/list']);
          return false;
        } else {
          // Other roles without projectId, redirect to '/home'
          this.router.navigate(['/home']);
          return false;
        }
      }

      // For non-'project' routes
      if (hasProjectId) {
        // Check if user has one of the required roles
        if (
          !requiredRoles ||
          requiredRoles.some((role) => userInfo.role === role)
        ) {
          return true;
        }
      } else {
        // User does not have the required role, redirect to home
        this.router.navigate(['/home']);
        return false;
      }
    }

    // User is not authenticated, redirect to login
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false; // Return false if user is not authenticated
  }
}
